import { employerInfoSchema } from '@df/auth';
import {
  caseIdSchema,
  type CaseId,
  clientIdSchema,
  companySchema,
  companyTypeSchema,
  corporateIdSchema,
  employerIdSchema,
  timeStampSchema,
  Time,
  type Amount,
} from '@df/common';
import { z } from 'zod';
import { zodDecoder } from '$lib/services/api/zod_decoder';
import { visibilityByPageSchema } from './firm/employer_settings.type';
import {
  accountingRegulationSchema,
  bookYearSchema,
  DEFAULT_REGULATION,
  mapFromRecord,
  type AccountingRegulation,
} from './schemas';

export const clientBaseInfoSchema = z.object({
  corporateId: corporateIdSchema,
  companyType: companyTypeSchema,
  companyName: z.string(),
  city: z.string(),
});
export type ClientBaseInfo = z.infer<typeof clientBaseInfoSchema>;
export const clientBaseInfoDecoder = zodDecoder(clientBaseInfoSchema);

// Request-only payloads: never decoded from a response, so they are plain types
// (the book year is sent as its canonical string; the backend accepts it).
export type ClientEconomicInfoUpdate = {
  bookYear: Time.BookYear;
  shareCapital?: Amount;
};

export type ClientInfoUpdate = {
  baseInfo: ClientBaseInfo;
  economicInfo?: ClientEconomicInfoUpdate;
};

export const engagementStatusSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Open') }),
  z.object({ type: z.literal('Closed'), closedAt: timeStampSchema }),
]);

export const engagementCaseSchema = z.object({
  caseId: caseIdSchema,
  status: engagementStatusSchema,
});
export type EngagementCase = z.infer<typeof engagementCaseSchema>;

export const engagementCasesSchema = mapFromRecord(Time.BookYear.schema, engagementCaseSchema); // key = BookYear
export type EngagementCases = z.infer<typeof engagementCasesSchema>;

/**
 * The newest of these book years.
 *
 * `compare` is end-date-major and breaks a tie on the start date, which `compareLast` leaves equal
 * — a short first year and a full one can end on the same day, and then the later start is the
 * newer file.
 */
function latestBookYearOf(bookYears: Iterable<Time.BookYear>): Time.BookYear | undefined {
  let latest: Time.BookYear | undefined;
  for (const bookYear of bookYears) {
    if (latest === undefined || Time.BookYear.compare(bookYear, latest) > 0) {
      latest = bookYear;
    }
  }
  return latest;
}

/**
 * The case for the client's latest year file.
 *
 * The map is built from the record the server sent, so its insertion order is the server's row
 * order and its last entry is not the newest year. Ask the book years which one that is.
 */
export function latestEngagement(engagements: EngagementCases): CaseId | undefined {
  const latest = latestBookYearOf(engagements.keys());
  return latest === undefined ? undefined : engagements.get(latest)?.caseId;
}

const onboardingActivities = ['BaseInformation', 'NaturalPersons', 'BookYear'] as const;
const onboardingActivitySchema = z.enum(onboardingActivities);
export type OnboardingActivity = z.infer<typeof onboardingActivitySchema>;
export function isOnboardingActivity(arg: unknown): arg is OnboardingActivity {
  return typeof arg === 'string' && onboardingActivities.includes(arg as OnboardingActivity);
}

const bookYearLedgerStatusSchema = z.enum(['HasLedger', 'NoLedger']);

const bookYearInfoSchema = z.object({
  status: bookYearLedgerStatusSchema,
  regulation: accountingRegulationSchema.nullable(),
});
export type BookYearInfo = z.infer<typeof bookYearInfoSchema>;

const clientContextSettingsSchema = z.object({ visibility: visibilityByPageSchema });
export type ClientContextSettings = z.infer<typeof clientContextSettingsSchema>;

export const clientContextSchema = z.object({
  clientId: clientIdSchema,
  company: companySchema,
  companyType: companyTypeSchema,
  employer: employerInfoSchema,
  auditEngagements: engagementCasesSchema,
  mainBookYear: bookYearSchema.nullable(),
  bookYears: mapFromRecord(Time.BookYear.schema, bookYearInfoSchema),
  hasLedger: z.boolean(),
  remainingOnboardingActivities: z.array(onboardingActivitySchema),
  settings: clientContextSettingsSchema,
});
export type ClientContext = z.infer<typeof clientContextSchema>;
export const clientContextDecoder = zodDecoder(clientContextSchema);
export function getBookYearsWithLedger(ctx: ClientContext): Time.BookYear[] {
  return [...ctx.bookYears].filterMap(([bky, info]) => (info.status === 'HasLedger' ? bky : null));
}

/** The regulation a book year's settings name, falling back to the client's most recent one. */
export function getRegulation(
  ctx: Pick<ClientContext, 'bookYears'>,
  bookYear: Time.BookYear,
): AccountingRegulation {
  const named = ctx.bookYears.get(bookYear)?.regulation;
  if (named !== null && named !== undefined) return named;
  const latest = [...ctx.bookYears.values()].filterMap((info) => info.regulation).at(-1);
  return latest ?? DEFAULT_REGULATION;
}
export function getLatestBookYear(ctx: ClientContext): Time.BookYear | null {
  return latestBookYearOf(ctx.bookYears.keys()) ?? null;
}

export type OpenEngagement = { bookYear: Time.BookYear; caseId: CaseId };

/** The client's running audits, oldest year first. */
export function getOpenEngagements(ctx: Pick<ClientContext, 'auditEngagements'>): OpenEngagement[] {
  return [...ctx.auditEngagements]
    .filterMap(([bookYear, engagement]) =>
      engagement.status.type === 'Open' ? { bookYear, caseId: engagement.caseId } : null,
    )
    .sort((first, second) => Time.BookYear.compare(first.bookYear, second.bookYear));
}

const employerClientsSchema = mapFromRecord(employerIdSchema, z.array(clientContextSchema));
export type EmployerClients = z.infer<typeof employerClientsSchema>;
export const employerClientsDecoder = zodDecoder(employerClientsSchema);
